import React from 'react';
import { createUseStyles } from 'react-jss';
import { S, A, DARK } from './tokens';

export type SegmentOption<T extends string> = { value: T; label: string; count?: number | null; testId?: string };

type Props<T extends string> = {
  value: T;
  options: Array<SegmentOption<T>>;
  onChange: (value: T) => void;
  label: string;
  className?: string;
};

const useStyles = createUseStyles({
  root: {
    display: 'inline-flex',
    padding: 3,
    gap: 2,
    borderRadius: 10,
    background: S.chromeHover,
    fontFamily: S.ui,
    maxWidth: '100%',
    overflowX: 'auto'
  },
  item: {
    all: 'unset',
    boxSizing: 'border-box',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    height: 30,
    padding: '0 12px',
    borderRadius: 7,
    fontSize: 14,
    fontWeight: 500,
    color: '#4A4D53',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    '&:hover': { color: S.fg },
    '&:focus-visible': A.focus,
    '&[aria-checked="true"]': {
      background: A.surface,
      color: S.fg,
      fontWeight: 600,
      boxShadow: '0 1px 2px rgba(23, 26, 32, 0.12), 0 0 0 1px rgba(23, 26, 32, 0.04)'
    },
    [DARK]: {
      color: S.muted,
      '&:hover': { color: S.fg },
      '&[aria-checked="true"]': { color: S.fg, boxShadow: `0 0 0 1px ${S.line}` }
    }
  },
  count: { fontSize: 12, fontWeight: 600, color: S.muted, fontVariantNumeric: 'tabular-nums' }
});

/** A small set of mutually exclusive views, e.g. which site's pages to list. Arrow keys move between them. */
function Segmented<T extends string>({ value, options, onChange, label, className }: Props<T>) {
  const c = useStyles();
  const onKeyDown = (e: React.KeyboardEvent) => {
    const i = options.findIndex(o => o.value === value);
    const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
    if (!step) return;
    e.preventDefault();
    const next = options[(i + step + options.length) % options.length];
    onChange(next.value);
    const btn = (e.currentTarget as HTMLElement).querySelector<HTMLElement>(`[data-value="${next.value}"]`);
    if (btn) btn.focus();
  };
  return (
    <div role="radiogroup" aria-label={label} className={[c.root, className].filter(Boolean).join(' ')} onKeyDown={onKeyDown}>
      {options.map(o => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={o.value === value}
          tabIndex={o.value === value ? 0 : -1}
          className={c.item}
          data-value={o.value}
          data-testid={o.testId}
          onClick={() => onChange(o.value)}
        >
          {o.label}
          {o.count != null && <span className={c.count}>{o.count.toLocaleString()}</span>}
        </button>
      ))}
    </div>
  );
}

export default Segmented;
